import { useState, useEffect, useRef } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from './supabase'
import './Dashboard.css'

const Dashboard = () => {
  const { user, signOut } = useAuth()
  const [view, setView] = useState('forms') // 'forms', 'submissions', 'submission-detail'
  const [forms, setForms] = useState([])
  const [formSubmissions, setFormSubmissions] = useState([])
  const [selectedForm, setSelectedForm] = useState(null)
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [pageSize] = useState(10) // Forms per page
  const [submissionPageSize] = useState(20) // Submissions per page
  const [submissionCurrentPage, setSubmissionCurrentPage] = useState(1)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  
  // Form creation states
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createFormName, setCreateFormName] = useState('')
  const [creatingForm, setCreatingForm] = useState(false)
  const [generatedForms, setGeneratedForms] = useState([]) // Store generated forms
  const [showGeneratedScript, setShowGeneratedScript] = useState(false)
  const [generatedScript, setGeneratedScript] = useState('')
  const [generatedFormName, setGeneratedFormName] = useState('')
  
  // Ref to prevent duplicate API calls
  const requestInProgress = useRef(false)

  // Fetch forms grouped by form_id with submission counts
  useEffect(() => {
    if (view === 'forms' && !requestInProgress.current) {
      fetchForms()
    }
  }, [view, currentPage])

  // Fetch submissions for selected form
  useEffect(() => {
    if (view === 'submissions' && selectedForm && !requestInProgress.current) {
      fetchFormSubmissions()
    }
  }, [view, selectedForm, submissionCurrentPage])

  const fetchForms = async () => {
    if (requestInProgress.current) return
    
    try {
      requestInProgress.current = true
      setLoading(true)
      setError('')
      
      const from = (currentPage - 1) * pageSize
      const to = from + pageSize - 1

      // Fetch form submissions
      const { data: submissions, error: submissionsError, count: submissionsCount } = await supabase
        .from('form_submissions')
        .select('form_id, created_at, data', { count: 'exact' })
        .not('form_id', 'is', null)
        .order('created_at', { ascending: false })
        .range(from, to)

      // Fetch generated forms
      const { data: generatedForms, error: generatedError } = await supabase
        .from('generated_forms')
        .select('*')
        .eq('user_id', user?.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (submissionsError && submissionsError.code !== 'PGRST116') {
        console.warn('Error fetching submissions:', submissionsError.message)
      }

      if (generatedError && generatedError.code !== 'PGRST116') {
        console.warn('Error fetching generated forms:', generatedError.message)
      }

      // Group submissions by form_id
      const formGroups = {}
      if (submissions) {
        submissions.forEach(submission => {
          if (!formGroups[submission.form_id]) {
            formGroups[submission.form_id] = {
              form_id: submission.form_id,
              submission_count: 1,
              latest_submission: submission.created_at,
              sample_data: submission.data,
              type: 'submission_form'
            }
          } else {
            formGroups[submission.form_id].submission_count++
            if (new Date(submission.created_at) > new Date(formGroups[submission.form_id].latest_submission)) {
              formGroups[submission.form_id].latest_submission = submission.created_at
              formGroups[submission.form_id].sample_data = submission.data
            }
          }
        })
      }

      // Add generated forms
      const formsArray = Object.values(formGroups)
      if (generatedForms) {
        generatedForms.forEach(genForm => {
          // Check if this form already has submissions
          const existingForm = formsArray.find(f => f.form_id === genForm.form_id)
          if (existingForm) {
            existingForm.type = 'generated_form'
            existingForm.generated_form_data = genForm
          } else {
            formsArray.push({
              form_id: genForm.form_id,
              name: genForm.name,
              submission_count: 0,
              latest_submission: genForm.created_at,
              sample_data: null,
              type: 'generated_form',
              generated_form_data: genForm
            })
          }
        })
      }

      // Sort by latest activity
      formsArray.sort((a, b) => new Date(b.latest_submission) - new Date(a.latest_submission))

      setForms(formsArray)
      setGeneratedForms(generatedForms || [])
      setTotalCount(formsArray.length)
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message)
    } finally {
      setLoading(false)
      requestInProgress.current = false
    }
  }

  const fetchFormSubmissions = async () => {
    if (requestInProgress.current) return
    
    try {
      requestInProgress.current = true
      setLoading(true)
      setError('')
      
      const from = (submissionCurrentPage - 1) * submissionPageSize
      const to = from + submissionPageSize - 1

      const { data, error, count } = await supabase
        .from('form_submissions')
        .select('*', { count: 'exact' })
        .eq('form_id', selectedForm.form_id)
        .order('created_at', { ascending: false })
        .range(from, to)

      if (error) {
        setError('Error fetching submissions: ' + error.message)
      } else {
        setFormSubmissions(data || [])
        setTotalCount(count || 0)
      }
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message)
    } finally {
      setLoading(false)
      requestInProgress.current = false
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  const refreshData = () => {
    if (view === 'forms') {
      setCurrentPage(1)
      fetchForms()
    } else {
      setSubmissionCurrentPage(1)
      fetchFormSubmissions()
    }
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const selectForm = (form) => {
    setSelectedForm(form)
    setSubmissionCurrentPage(1)
    setView('submissions')
  }

  const selectSubmission = (submission) => {
    setSelectedSubmission(submission)
    setView('submission-detail')
  }

  const goBackToForms = () => {
    setView('forms')
    setSelectedForm(null)
    setSelectedSubmission(null)
    setCurrentPage(1)
    setSubmissionCurrentPage(1)
  }

  const goBackToSubmissions = () => {
    setView('submissions')
    setSelectedSubmission(null)
  }

  // Form creation functions
  const handleCreateForm = () => {
    setShowCreateForm(true)
    setCreateFormName('')
  }

  const handleCreateFormSubmit = async (e) => {
    e.preventDefault()
    if (!createFormName.trim()) {
      setError('Please enter a form name')
      return
    }

    setCreatingForm(true)
    setError('')

    try {
      const formId = createFormName.trim().toLowerCase().replace(/[^a-z0-9]/g, '-')
      const script = generateFormScript(formId)
      
      // Create form record in database
      const { data, error } = await supabase
        .from('generated_forms')
        .insert([
          {
            name: createFormName.trim(),
            form_id: formId,
            user_id: user?.id,
            created_at: new Date().toISOString(),
            html_content: script,
            is_active: true
          }
        ])
        .select()

      if (error) {
        setError('Error creating form: ' + error.message)
        return
      }

      // Add to local state
      setGeneratedForms(prev => [...prev, data[0]])
      setShowCreateForm(false)
      setCreateFormName('')
      
      // Show the generated script
      setGeneratedScript(script)
      setGeneratedFormName(createFormName.trim())
      setShowGeneratedScript(true)
      
      // Refresh forms list
      refreshData()
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message)
    } finally {
      setCreatingForm(false)
    }
  }

  const generateFormScript = (formId) => {
    return `<script>
    (W = 'https://formcapture.vikas-580.workers.dev/', K = 'BhasdBhhbhsdgHYGYyuysadYGAsdy8787asdhbBagsdhG773432dhbHbdHASDBH&YGUJBASD7yB8SCU', addEventListener('DOMContentLoaded', _ => document.querySelectorAll('form').forEach(f => f.onsubmit = async e => (e.preventDefault(), b = f.querySelector('[type=submit],button'), t = b.textContent || b.value, d = new FormData(f), b.disabled = !0, b.textContent = b.value = 'Sending...', d.append('site_id', location.hostname), d.append('form_id', '${formId}'), d.append('page_url', location.href), d.append('timestamp', new Date().toISOString()), fetch(W, {
        method: 'POST',
        headers: {
            'X-API-Key': K
        },
        body: d
    }).then(r => r.json()).then(j => (alert(j.success ? '✅ Thank you! Your message has been sent.' : '❌ Error: ' + j.error), j.success && f.reset())).catch(_ => alert('❌ Network error. Please try again.')).finally(_ => (b.disabled = !1, b.textContent = b.value = t))))))
</script>`
  }

  const copyScript = async (form) => {
    try {
      await navigator.clipboard.writeText(form.html_content)
      // Show temporary success message
      const button = document.querySelector(`[data-form-id="${form.id}"]`)
      if (button) {
        const originalText = button.textContent
        button.textContent = '✅ Copied!'
        button.style.backgroundColor = '#28a745'
        setTimeout(() => {
          button.textContent = originalText
          button.style.backgroundColor = ''
        }, 2000)
      }
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = form.html_content
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      
      // Show success message
      alert('Script copied to clipboard!')
    }
  }

  const renderJsonData = (jsonData) => {
    if (!jsonData) return 'N/A'
    
    try {
      const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData
      return (
        <div className="json-data">
          {Object.entries(parsed).map(([key, value]) => (
            <div key={key} className="json-field">
              <span className="json-key">{key}:</span>
              <span className="json-value">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </span>
            </div>
          ))}
        </div>
      )
    } catch (e) {
      return <span className="json-error">Invalid JSON: {String(jsonData)}</span>
    }
  }

  const renderFiles = (files) => {
    if (!files) return 'N/A'
    
    try {
      const fileData = typeof files === 'string' ? JSON.parse(files) : files
      
      if (Array.isArray(fileData)) {
        return (
          <div className="files-list">
            {fileData.map((file, index) => {
              // Handle both URL strings and file objects
              if (typeof file === 'string') {
                return (
                  <div key={index} className="file-item">
                    <button 
                      className="file-button"
                      onClick={() => window.open(file, '_blank')}
                    >
                      📎 File {index + 1}
                    </button>
                    <div className="file-url">{file}</div>
                  </div>
                )
              } else if (typeof file === 'object' && file !== null) {
                // Handle file objects with metadata
                const fileUrl = file.publicUrl || file.url || file.path || ''
                const fileName = file.filename || file.name || `File ${index + 1}`
                const fileSize = file.size ? ` (${Math.round(file.size / 1024)}KB)` : ''
                
                return (
                  <div key={index} className="file-item">
                    <button 
                      className="file-button"
                      onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                      disabled={!fileUrl}
                    >
                      📎 {fileName}{fileSize}
                    </button>
                    {fileUrl && <div className="file-url">{fileUrl}</div>}
                    {file.contentType && (
                      <div className="file-meta">Type: {file.contentType}</div>
                    )}
                  </div>
                )
              }
              return null
            })}
          </div>
        )
      } else if (typeof fileData === 'object' && fileData !== null) {
        return (
          <div className="files-list">
            {Object.entries(fileData).map(([key, file]) => {
              // Handle both URL strings and file objects
              if (typeof file === 'string') {
                return (
                  <div key={key} className="file-item">
                    <button 
                      className="file-button"
                      onClick={() => window.open(file, '_blank')}
                    >
                      📎 {key}
                    </button>
                    <div className="file-url">{file}</div>
                  </div>
                )
              } else if (typeof file === 'object' && file !== null) {
                // Handle file objects with metadata
                const fileUrl = file.publicUrl || file.url || file.path || ''
                const fileName = file.filename || file.name || key
                const fileSize = file.size ? ` (${Math.round(file.size / 1024)}KB)` : ''
                
                return (
                  <div key={key} className="file-item">
                    <button 
                      className="file-button"
                      onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                      disabled={!fileUrl}
                    >
                      📎 {fileName}{fileSize}
                    </button>
                    {fileUrl && <div className="file-url">{fileUrl}</div>}
                    {file.contentType && (
                      <div className="file-meta">Type: {file.contentType}</div>
                    )}
                  </div>
                )
              }
              return null
            })}
          </div>
        )
      }
      
      // Fallback for other data types
      return <div className="file-fallback">{String(fileData)}</div>
    } catch (e) {
      return <span className="files-error">Invalid file data: {e.message}</span>
    }
  }

  // Pagination calculations
  const currentPageForCalc = view === 'forms' ? currentPage : submissionCurrentPage
  const pageSizeForCalc = view === 'forms' ? pageSize : submissionPageSize
  const totalPages = Math.ceil(totalCount / pageSizeForCalc)
  const startRecord = totalCount === 0 ? 0 : (currentPageForCalc - 1) * pageSizeForCalc + 1
  const endRecord = Math.min(currentPageForCalc * pageSizeForCalc, totalCount)

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      if (view === 'forms') {
        setCurrentPage(page)
      } else {
        setSubmissionCurrentPage(page)
      }
    }
  }

  const goToPrevious = () => {
    if (view === 'forms' && currentPage > 1) {
      setCurrentPage(currentPage - 1)
    } else if (view === 'submissions' && submissionCurrentPage > 1) {
      setSubmissionCurrentPage(submissionCurrentPage - 1)
    }
  }

  const goToNext = () => {
    if (view === 'forms' && currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    } else if (view === 'submissions' && submissionCurrentPage < totalPages) {
      setSubmissionCurrentPage(submissionCurrentPage + 1)
    }
  }

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="sidebar-header">
          <h2>Dashboard</h2>
          <button onClick={toggleSidebar} className="sidebar-toggle-btn">
            <span className="arrow-icon">{sidebarOpen ? '←' : '→'}</span>
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <ul>
            <li><a href="#" className="nav-item active">Forms</a></li>
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <p className="user-email">{user?.email}</p>
          </div>
          <button onClick={handleSignOut} className="logout-btn">
            Sign Out
          </button>
        </div>
      </div>

      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* Toggle button for when sidebar is closed */}
      {!sidebarOpen && (
        <button onClick={toggleSidebar} className="sidebar-toggle-floating">
          <span className="arrow-icon">→</span>
        </button>
      )}

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'content-with-sidebar' : 'content-full-width'}`}>
        <div className="content-header">
          <div className="header-title">
            {view === 'forms' ? (
              <h1>Forms Overview</h1>
            ) : view === 'submissions' ? (
              <div className="breadcrumb">
                <button onClick={goBackToForms} className="breadcrumb-btn">
                  ← Forms
                </button>
                <h1>Form: {selectedForm?.form_id}</h1>
              </div>
            ) : (
              <div className="breadcrumb">
                <button onClick={goBackToForms} className="breadcrumb-btn">
                  ← Forms
                </button>
                <span className="breadcrumb-separator">/</span>
                <button onClick={goBackToSubmissions} className="breadcrumb-btn">
                  ← {selectedForm?.form_id}
                </button>
                <h1>Submission Details</h1>
              </div>
            )}
          </div>
          <div className="header-actions">
            {view === 'forms' && (
              <button onClick={handleCreateForm} className="create-form-btn">
                + Create Form
              </button>
            )}
            <button onClick={refreshData} className="refresh-btn" disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
        
        <div className="content-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {loading && !error ? (
            <div className="loading-message">
              {view === 'forms' ? 'Loading forms...' : view === 'submission-detail' ? 'Loading submission detail...' : 'Loading submissions...'}
            </div>
          ) : (
            <div className="data-container">
              {view === 'forms' ? (
                // Forms List View
                <>
                  {forms.length === 0 ? (
                    <div className="no-data-message">
                      No forms found.
                    </div>
                  ) : (
                    <>
                      <div className="forms-grid">
                        {forms.map((form) => (
                          <div key={form.form_id} className={`form-card ${form.type === 'generated_form' ? 'generated-form-card' : ''}`}>
                            <div className="form-card-header">
                              <h3>{form.name || form.form_id}</h3>
                              <div className="form-card-meta">
                                {form.type === 'generated_form' && (
                                  <span className="form-type-badge">Generated</span>
                                )}
                                <span className="submission-count">{form.submission_count} submissions</span>
                              </div>
                            </div>
                            <div className="form-card-body">
                              <p className="latest-submission">
                                {form.type === 'generated_form' ? 'Created' : 'Latest'}: {new Date(form.latest_submission).toLocaleString()}
                              </p>
                              {form.type === 'generated_form' && (
                                <p className="form-id-info">
                                  Form ID: <code>{form.form_id}</code>
                                </p>
                              )}
                            </div>
                            <div className="form-card-footer">
                              {form.type === 'generated_form' ? (
                                <div className="generated-form-actions">
                                  <button 
                                    className="copy-script-btn"
                                    data-form-id={form.generated_form_data.id}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      copyScript(form.generated_form_data)
                                    }}
                                  >
                                    📋 Copy Script
                                  </button>
                                  {form.submission_count > 0 && (
                                    <button 
                                      className="view-submissions-btn"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        selectForm(form)
                                      }}
                                    >
                                      View Submissions →
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="view-link" onClick={() => selectForm(form)}>View Submissions →</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : view === 'submissions' ? (
                // Submissions List View (Compact)
                <>
                  {formSubmissions.length === 0 ? (
                    <div className="no-data-message">
                      No submissions found for this form.
                    </div>
                  ) : (
                    <>
                      <div className="submissions-table-container">
                        <table className="submissions-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Data Summary</th>
                              <th>Files</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formSubmissions.map((submission, index) => (
                              <tr key={submission.id || index}>
                                <td className="date-cell">
                                  {new Date(submission.created_at).toLocaleString()}
                                </td>
                                <td className="summary-cell">
                                  {submission.data ? (
                                    <div className="data-preview">
                                      {(() => {
                                        try {
                                          const data = typeof submission.data === 'string' ? 
                                            JSON.parse(submission.data) : submission.data
                                          
                                          // Get first 2 values, prioritizing common fields like name and email
                                          const entries = Object.entries(data)
                                          const priorityFields = ['name', 'email', 'firstName', 'lastName', 'fullName', 'phone']
                                          
                                          // Sort entries to prioritize common fields
                                          const sortedEntries = entries.sort((a, b) => {
                                            const aIndex = priorityFields.indexOf(a[0].toLowerCase())
                                            const bIndex = priorityFields.indexOf(b[0].toLowerCase())
                                            if (aIndex === -1 && bIndex === -1) return 0
                                            if (aIndex === -1) return 1
                                            if (bIndex === -1) return -1
                                            return aIndex - bIndex
                                          })
                                          
                                          // Take first 2 entries and format their values
                                          const previewValues = sortedEntries.slice(0, 2).map(([key, value]) => {
                                            if (value === null || value === undefined || value === '') {
                                              return `${key}: (empty)`
                                            }
                                            // Truncate long values
                                            const stringValue = String(value)
                                            return stringValue.length > 25 ? 
                                              stringValue.substring(0, 25) + '...' : 
                                              stringValue
                                          })
                                          
                                          const result = previewValues.join(' • ')
                                          return result || 'No data'
                                        } catch (e) {
                                          return 'Invalid data'
                                        }
                                      })()}
                                    </div>
                                  ) : 'No data'}
                                </td>
                                <td className="files-cell">
                                  {submission.files ? (
                                    <span className="file-count">
                                      📎 {(() => {
                                        try {
                                          const files = typeof submission.files === 'string' ? 
                                            JSON.parse(submission.files) : submission.files
                                          return Array.isArray(files) ? 
                                            files.length : 
                                            Object.keys(files).length
                                        } catch (e) {
                                          return '?'
                                        }
                                      })()} files
                                    </span>
                                  ) : 'No files'}
                                </td>
                                <td className="action-cell">
                                  <button 
                                    className="view-details-btn"
                                    onClick={() => selectSubmission(submission)}
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              ) : (
                // Submission Detail View
                <>
                  {selectedSubmission && (
                    <div className="submission-detail">
                      <div className="submission-detail-header">
                        <span className="submission-detail-date">
                          {new Date(selectedSubmission.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="submission-detail-content">
                        <div className="form-data-section">
                          <h4>Form Data:</h4>
                          {renderJsonData(selectedSubmission.data)}
                        </div>
                        {selectedSubmission.files && (
                          <div className="files-section">
                            <h4>Files:</h4>
                            {renderFiles(selectedSubmission.files)}
                          </div>
                        )}
                        {selectedSubmission.metadata && (
                          <div className="metadata-section">
                            <h4>Metadata:</h4>
                            <div className="metadata-content">
                              {typeof selectedSubmission.metadata === 'object' ? 
                                JSON.stringify(selectedSubmission.metadata, null, 2) : 
                                selectedSubmission.metadata}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Pagination Controls - Only show for forms and submissions list */}
              {(view === 'forms' || view === 'submissions') && totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {startRecord} to {endRecord} of {totalCount} {view === 'forms' ? 'forms' : 'submissions'}
                  </div>
                  
                  <div className="pagination-controls">
                    <button 
                      onClick={goToPrevious} 
                      disabled={currentPageForCalc === 1}
                      className="pagination-btn"
                    >
                      Previous
                    </button>
                    
                    <div className="pagination-pages">
                      {/* Show first page */}
                      {currentPageForCalc > 3 && (
                        <>
                          <button onClick={() => goToPage(1)} className="pagination-btn">
                            1
                          </button>
                          {currentPageForCalc > 4 && <span className="pagination-ellipsis">...</span>}
                        </>
                      )}
                      
                      {/* Show pages around current page */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = Math.max(1, currentPageForCalc - 2) + i
                        if (page <= totalPages && page >= Math.max(1, currentPageForCalc - 2) && page <= Math.min(totalPages, currentPageForCalc + 2)) {
                          return (
                            <button
                              key={page}
                              onClick={() => goToPage(page)}
                              className={`pagination-btn ${currentPageForCalc === page ? 'active' : ''}`}
                            >
                              {page}
                            </button>
                          )
                        }
                        return null
                      })}
                      
                      {/* Show last page */}
                      {currentPageForCalc < totalPages - 2 && (
                        <>
                          {currentPageForCalc < totalPages - 3 && <span className="pagination-ellipsis">...</span>}
                          <button onClick={() => goToPage(totalPages)} className="pagination-btn">
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    
                    <button 
                      onClick={goToNext} 
                      disabled={currentPageForCalc === totalPages}
                      className="pagination-btn"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Form</h3>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowCreateForm(false)}
                disabled={creatingForm}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateFormSubmit} className="create-form-form">
              <div className="form-group">
                <label htmlFor="formName">Form Name *</label>
                <input
                  type="text"
                  id="formName"
                  value={createFormName}
                  onChange={(e) => setCreateFormName(e.target.value)}
                  placeholder="Enter form name (e.g., Contact Form, Newsletter Signup)"
                  required
                  disabled={creatingForm}
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowCreateForm(false)} 
                  className="cancel-btn"
                  disabled={creatingForm}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="create-btn"
                  disabled={creatingForm || !createFormName.trim()}
                >
                  {creatingForm ? 'Creating...' : 'Create Form'}
                </button>
              </div>
            </form>
                     </div>
         </div>
       )}

      {/* Generated Script Modal */}
      {showGeneratedScript && (
        <div className="modal-overlay" onClick={() => setShowGeneratedScript(false)}>
          <div className="modal-content script-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Script Generated for "{generatedFormName}"</h3>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowGeneratedScript(false)}
              >
                ×
              </button>
            </div>
            <div className="script-modal-body">
              <p className="script-instructions">
                Copy this script and paste it into any website where you have forms. 
                It will automatically capture form submissions and send them to your dashboard.
              </p>
              
              <div className="script-container">
                <div className="script-header">
                  <span className="script-label">Generated Script:</span>
                  <button 
                    className="copy-script-modal-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(generatedScript)
                        const btn = document.querySelector('.copy-script-modal-btn')
                        const originalText = btn.textContent
                        btn.textContent = '✅ Copied!'
                        btn.style.backgroundColor = '#28a745'
                        setTimeout(() => {
                          btn.textContent = originalText
                          btn.style.backgroundColor = ''
                        }, 2000)
                      } catch (err) {
                        alert('Script copied to clipboard!')
                      }
                    }}
                  >
                    📋 Copy Script
                  </button>
                </div>
                <pre className="script-display">{generatedScript}</pre>
              </div>
              
              <div className="script-usage">
                <h4>How to use:</h4>
                <ol>
                  <li>Copy the script above</li>
                  <li>Paste it before the closing <code>&lt;/body&gt;</code> tag on any website</li>
                  <li>The script will automatically detect and handle all forms on that page</li>
                  <li>Form submissions will appear in your dashboard with the name "{generatedFormName}"</li>
                </ol>
              </div>
              
              <div className="form-actions">
                <button 
                  onClick={() => setShowGeneratedScript(false)} 
                  className="close-modal-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard 